<template>
  <div
    v-if="chartDataQuery.loading.value"
    class="_loading-container"
  >
    <m-spinner />
  </div>
  <div
    v-else-if="chartDataQuery.error.value !== null"
    class="_alert"
  >
    <m-alert
      type="error"
      :message="chartDataQuery.error.value"
    />
  </div>
  <div
    v-else-if="chartDataQuery.hasOrphanRef.value"
    class="_orphan"
  >
    <m-icon
      type="warning"
      :color="$colors.grey.lighten1"
      size="48"
    />
    <div class="_orphan-message">
      {{ $t('gridPageChart.orphanRefMessage') }}
    </div>
    <m-btn
      v-if="canFixOrphan"
      @click="$emit('fix-orphan')"
    >
      {{ $t('gridPageChart.orphanRefHelp') }}
    </m-btn>
  </div>
  <MetricDisplay
    v-else-if="chart.chartType === gridPageChartType.metric"
    :value="metricDisplayValue"
    :unit="unit"
    :loading="chartDataQuery.loading.value"
    :size="size"
    :color="metricColor"
    :title="metricTitle"
    :title-enabled="metricTitleEnabled"
    :clickable="isMetricClickable"
    @activate="handleMetricClick"
  />
  <GridPageChartDataTable
    v-else-if="chart.chartType === gridPageChartType.table"
    :chart="chart"
    :chart-data-query="chartDataQuery"
  />
  <chart
    v-else
    :key="`${chart.chartType}-${aggregator}-${chart.stacked ?? true}`"
    :options="chartOptions"
    :class="['_chart', { '-clickable': isClickable }]"
  />
  <m-dialog
    v-model:value="showGoalList"
    :max-width="maxDialogWidth"
    hide-footer
    no-padding
    keep-height
    center
    :title="goalListTitle"
  >
    <temp-goal-list
      v-if="showGoalList"
      :id="tempGoalListId"
      :default-filter="goalListFilter"
      :title="goalListTitle"
      :goal-cycles="goalListCycles"
      :hide-cycle-selector="hideCycleSelector"
      :force-props-visibility-by-key="goalListVisibleProps"
    />
  </m-dialog>
</template>

<script setup>
import GridPageChartDataTable from '@/components/custom-grid/GridPageChartDataTable.vue';
import MetricDisplay from '@/components/custom-grid/chart/MetricDisplay.vue';
import TempGoalList from '@/components/TempGoalList.vue';
import useBarChart from '@/composables/grid-page/chart/bar-chart';
import useChartDisplay from '@/composables/chart-display';
import useChartPointClick from '@/composables/grid-page/chart/chart-point-click';
import useChartValueFormatter from '@/composables/grid-page/chart/chart-value-formatter';
import useGoalProgressChart from '@/composables/grid-page/chart/goal-progress-chart';
import useLineChart from '@/composables/grid-page/chart/line-chart';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePieChart from '@/composables/grid-page/chart/pie-chart';
import useProperties from '@/composables/property/property';
import useStatusDistributionChart from '@/composables/grid-page/chart/status-distribution-chart';
import useStatusScoreChart from '@/composables/grid-page/chart/status-score-chart';
import { Chart } from 'highcharts-vue';
import { TIME_SERIES_AGGREGATORS } from '@/lib/constants';
import { aggregatorType, coloringMode, gridPageChartType, propertyType } from 'shared/constants.json';
import { computed, toRef } from 'vue';
import { evaluateColoringRules } from '@/lib/coloring-rules';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { numberFormatAffix } from '@/lib/props/number';

const NUMERIC_PROPERTY_AGGREGATORS = [
  aggregatorType.sum,
  aggregatorType.avg,
  aggregatorType.min,
  aggregatorType.max,
];

const props = defineProps({
  chart: { type: Object, required: true },
  size: { type: String, default: 'medium', validator: (value) => ['small', 'medium', 'large'].includes(value) },
  chartDataQuery: { type: Object, required: true },
  idPrefix: { type: String, required: true },
  canFixOrphan: { type: Boolean, default: false },
});

defineEmits(['fix-orphan']);

const { loggedInUserAccount } = useLoggedInUserAccount();
const propertiesSvc = useProperties();

const {
  showGoalList,
  goalListFilter,
  goalListTitle,
  goalListCycles,
  hideCycleSelector,
  maxDialogWidth,
  tempGoalListId,
  handlePointClick: openGoalList,
  handleMetricClick,
  isClickable,
  goalListVisibleProps,
} = useChartPointClick({
  chart: toRef(props, 'chart'),
  chartDataQuery: props.chartDataQuery,
  accountUid: computed(() => loggedInUserAccount.value.uid),
  idPrefix: props.idPrefix,
});

const isMetricClickable = computed(() => props.chart.source === goalConfig.model
  && props.chart.chartType === gridPageChartType.metric);

const handlePointClick = computed(() => (isClickable.value
  ? (categoryIndex) => openGoalList({ categoryIndex })
  : undefined));

const aggregator = computed(() => props.chart.aggregationConfig?.aggregation?.aggregator);
const isTimeSeriesMode = computed(() => TIME_SERIES_AGGREGATORS.includes(aggregator.value));

const result = props.chartDataQuery.result;

const { chartOptions: pieChartOptions } = usePieChart(result, toRef(props, 'chart'), handlePointClick);
const { chartOptions: lineChartOptions } = useLineChart(result, toRef(props, 'chart'), handlePointClick);
const { chartOptions: barChartOptions } = useBarChart(result, toRef(props, 'chart'), handlePointClick);
const { chartOptions: progressChartOptions } = useGoalProgressChart(result, toRef(props, 'chart'));
const { chartOptions: statusScoreChartOptions } = useStatusScoreChart(result, toRef(props, 'chart'));
const { chartOptions: statusDistributionChartOptions } = useStatusDistributionChart(result, toRef(props, 'chart'), { showLegend: true });

const chartOptions = computed(() => {
  const baseOptions = { chart: { animation: false } };

  if (isTimeSeriesMode.value) {
    switch (aggregator.value) {
      case aggregatorType.statusScoreOverTime:
        return statusScoreChartOptions.value;
      case aggregatorType.statusDistributionOverTime:
        return statusDistributionChartOptions.value;
      case aggregatorType.progressOverTime:
      case aggregatorType.progressOverTimeUnbounded:
        return progressChartOptions.value;
      default:
        return baseOptions;
    }
  }

  switch (props.chart.chartType) {
    case gridPageChartType.column:
    case gridPageChartType.bar:
      return barChartOptions.value;
    case gridPageChartType.line:
      return lineChartOptions.value;
    case gridPageChartType.pie:
      return pieChartOptions.value;
    default:
      return baseOptions;
  }
});

const { formatValue } = useChartValueFormatter(toRef(props, 'chart'));

const metricResult = computed(() => {
  if (props.chart.chartType !== gridPageChartType.metric || props.chartDataQuery.result.value === null) {
    return null;
  }
  return props.chartDataQuery.result.value;
});

const metricNumberFormat = computed(() => {
  const aggregation = props.chart.aggregationConfig?.aggregation;
  if (!aggregation?.isProperty
    || !NUMERIC_PROPERTY_AGGREGATORS.includes(aggregation.aggregator)
    || !aggregation.property?.uid) {
    return null;
  }
  const property = propertiesSvc.selectSingle(aggregation.property.uid);
  if (property?.type !== propertyType.number) {
    return null;
  }
  return property.numberFormat;
});

const metricDisplayValue = computed(() => {
  const formatted = formatValue(metricResult.value);
  if (formatted === '' || !metricNumberFormat.value) {
    return formatted;
  }
  return numberFormatAffix(metricNumberFormat.value)(formatted);
});

const { metricUnit } = useChartDisplay();

const unit = computed(() => metricUnit(props.chart.aggregationConfig.aggregation, props.chart.source));

const metricColor = computed(() => {
  if (props.chart.coloringMode === coloringMode.dynamic) {
    const rules = (props.chart.coloringRules || [])
      .filter((r) => !r.deletedAt)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    return evaluateColoringRules(metricResult.value, rules);
  }
  return props.chart.color || null;
});

const metricTitleEnabled = computed(() => props.chart.chartType === gridPageChartType.metric
  && !!props.chart.metricTitleEnabled);

const metricTitle = computed(() => {
  if (!metricTitleEnabled.value) return '';
  return props.chart.metricTitle ?? '';
});
</script>

<style scoped lang="scss">
._alert {
  width: 100%;
}

._chart {
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
  min-height: 0;

  &.-clickable {
    :deep(.highcharts-plot-background) {
      cursor: pointer;
    }
  }
}

._loading-container {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

._orphan {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  padding: 2rem;
  text-align: center;
}

._orphan-message {
  color: $font-color-secondary;
  font-size: 1.4rem;
}
</style>
